<script setup lang="ts">
import Extension from "@/core/interfaces/extension/Extension";
import ExtensionLogo from "@/extension/components/extension-logo/ExtensionLogo.vue";
import {computed, ref} from "vue";
import {ShowcaseItem, ShowcaseType} from "@/core/interfaces/showcase";
import {extensionApi} from "@/container";
import EmbedVideoModalButton from "@/video/components/EmbedVideoModalButton.vue";
import VideoBox from "@/core/components/icons/VideoBox.vue";
import {Switch} from "@/core/components/ui/switch";
import ExtensionProfitabilityShowcase
    from "@/extension/components/extension-profitability-tab/ExtensionProfitabilityShowcase.vue";
import ExtensionPotentialShowcase
    from "@/extension/components/extension-profitability-tab/ExtensionPotentialShowcase.vue";

const props = defineProps<{
    extension: Extension;
    defaultItems: ShowcaseItem[];
}>();

defineEmits<{
    (event: 'extensionClick', extension: Extension): void,
    (event: 'marginChange', value: number): void;
}>();

const showcases = ref<ShowcaseItem[]>(props.defaultItems || []);

/**
 * Get the profitability showcase if it exists.
 */
const profitability = computed(() => {
    if (!showcases.value || showcases.value.length === 0) {
        return undefined;
    }

    return showcases.value.find(showcase => showcase.type === ShowcaseType.PROFITABILITY);
});

/**
 * Get the potential showcase if it exists.
 */
const potential = computed(() => {
    if (!showcases.value || showcases.value.length === 0) {
        return undefined;
    }

    return showcases.value.find(showcase => showcase.type === ShowcaseType.POTENTIAL);
});

/**
 * Determine if the sales pitch should be displayed in a row.
 */
const displaySalesPitchInRow = computed(() => {
    return potential.value === undefined && profitability.value === undefined;
});

/**
 * Enable the extension.
 */
const enableExtension = async (extension: Extension) => {
    extension.enabled = true;

    return extensionApi()
        .enable(extension.id)
        .catch(() => {
            extension.enabled = false;
        });
};

/**
 * Disable the extension.
 */
const disableExtension = async (extension: Extension) => {
    extension.enabled = false;

    return extensionApi()
        .disable(extension.id)
        .catch(() => {
            extension.enabled = true;
        });
};

const toggleExtension = async () => {
    if (props.extension.enabled) {
        await disableExtension(props.extension);
    } else {
        await enableExtension(props.extension);
    }
};
</script>

<template>
  <div class="space-y-1">
    <div class="grid grid-cols-4 bg-white rounded-lg overflow-hidden shadow-[3px_3px_0_0_#AB74E0] items-center gap-4">
      <div class="flex items-center gap-2">
        <ExtensionLogo
          :extension="extension"
          class="h-[45px] shrink-0 hover:cursor-pointer hover:shadow-2xl hover:shadow-primary transition-shadow duration-300"
          @click="$emit('extensionClick', extension)"
        />
        <p class="text-primary truncate">
          {{ extension.name }}
        </p>
        <div class="w-[20px] shrink-0">
          <EmbedVideoModalButton
            v-if="extension.video_url" :title="extension.name"
            :embed-video-url="extension.video_url"
          >
            <div class="rounded-full overflow-hidden shrink-0">
              <VideoBox :size="20" />
            </div>
          </EmbedVideoModalButton>
        </div>
      </div>
      <div
        class="flex justify-center"
        :class="{
          'col-span-2': displaySalesPitchInRow,
        }"
      >
        <div v-if="potential !== undefined">
          <ExtensionPotentialShowcase :item="potential" />
        </div>
        <div v-else-if="extension.sales_pitch" class="leading-none">
          <span class="text-sm font-medium text-primary">{{ extension.sales_pitch }}</span>
        </div>
      </div>
      <div v-if="!displaySalesPitchInRow">
        <ExtensionProfitabilityShowcase
          v-if="profitability && profitability.value as number > 0"
          :item="profitability"
          @margin-change="(value) => $emit('marginChange', value)"
        />
      </div>
      <div class="flex items-center justify-end pr-4">
        <Switch :model-value="extension.enabled" @update:model-value="toggleExtension" />
      </div>
    </div>
    <p v-if="!displaySalesPitchInRow && extension.sales_pitch">
      <span class="text-sm text-primary ml-2 font-medium">
        {{ extension.sales_pitch }}
      </span>
    </p>
  </div>
</template>

<style scoped>

</style>s
